import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { generalMessages } from 'src/registry/utils/messages';
import { WebhookEndpoint } from 'src/features/webhook-endpoints/utils/types';
import { usePatchWebhookEndpoint } from 'src/features/webhook-endpoints/api/usePatchWebhookEndpoint';
import { LIST_WEBHOOK_ENDPOINTS_RESOURCE } from 'src/features/webhook-endpoints/api/useListWebhookEndpoints';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';
import WebhookEndpointFormModal from 'src/features/webhook-endpoints/components/forms/WebhookEndpointFormModal';

interface UpdateWebhookEndpointFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWebhookEndpoint: WebhookEndpoint;
    children?: React.ReactNode;
}

function UpdateWebhookEndpointFormModal<TData>({
    className,
    semantics,
    children,
    selectedWebhookEndpoint,
}: UpdateWebhookEndpointFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutateAsync, isPending } = usePatchWebhookEndpoint({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_WEBHOOK_ENDPOINTS_RESOURCE] });
            toast({
                title: formatMessage(webhookEndpointsMessages.updateWebhookEndpointSuccess),
                description: formatMessage(webhookEndpointsMessages.updateWebhookEndpointSuccessDescription),
            });
        },
    });

    return (
        <WebhookEndpointFormModal
            selectedWebhookEndpoint={selectedWebhookEndpoint}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutateAsync({ data: values, endpointId: selectedWebhookEndpoint.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(webhookEndpointsMessages.editWebhookEndpointSubtitle)}
        >
            {children}
        </WebhookEndpointFormModal>
    );
}

export default UpdateWebhookEndpointFormModal;
